import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Box, Snackbar, Alert } from '@mui/material';
import { User } from '../types/type';
import { COLORS_ADMIN_PAGE } from '../../conf/constants';
import { useErrorHandler } from '../../utils/useErrorHandler';
import UserSidebar from './UserSidebar';
import UserProfile from './UserProfile';
import CreateUserModal from './CreateUserModal';
import dayjs from 'dayjs';
import ChangePasswordModal from './ChangePasswordModal';
import { ResizeHandle } from '../../conf/constants';
import dataProvider from '../../services/api';
import { USER_MANAGEMENT_CONSTANTS } from '../constants/useManagement';

const UserManagement: React.FC = () => {
  const { snackbar, handleErrorWithLogout, showSuccessMessage, handleSnackbarClose } = useErrorHandler();

  const [users, setUsers] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [openChangePasswordModal, setOpenChangePasswordModal] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState<string>(USER_MANAGEMENT_CONSTANTS.DEFAULT_SIDEBAR_WIDTH);
  const [isResizing, setIsResizing] = useState(false);
  const [usersPerPage, setUsersPerPage] = useState<number>(USER_MANAGEMENT_CONSTANTS.DEFAULT_USERS_PER_PAGE);
  const [isSearchMode, setIsSearchMode] = useState(false);
  
  const containerRef = useRef<HTMLDivElement>(null);
  const sidebarRef = useRef<HTMLDivElement>(null);

  const handleUsersPerPageChange = useCallback((newUsersPerPage: number) => {
    if (newUsersPerPage !== usersPerPage) {
      setUsersPerPage(newUsersPerPage);
      setCurrentPage(1);
    }
  }, [usersPerPage]);

  const parseSearchTerm = (term: string) => {
    const cleanTerm = term.trim().toLowerCase();
    const words = cleanTerm.split(/\s+/).filter(word => word.length > 0);
    
    return {
      originalTerm: cleanTerm,
      words: words,
      firstName: words[0] || '',
      lastName: words[words.length - 1] || '',
      middleName: words.length > 2 ? words.slice(1, -1).join(' ') : ''
    };
  };

  const searchWithKeyword = async (keyword: string) => {
    try {
      const { data } = await dataProvider.getListUsers({
        filter: keyword,
        page: 0,
        perPage: USER_MANAGEMENT_CONSTANTS.SEARCH_RESULTS_LIMIT, 
      });
      return data;
    } catch (error) {
      handleErrorWithLogout(error);
      return [];
    }
  };

  const isUserMatchSearchTerm = (user: User, searchTerm: string) => {
    const fullName = `${user.firstName} ${user.middleName || ''} ${user.lastName}`.trim().toLowerCase();
    const email = (user.email || '').toLowerCase();
    const phone = (user.phoneNumber || '').toLowerCase();
    const username = (user.username || '').toLowerCase();
    const nationality = (user.nationality || '').toLowerCase();
    const searchLower = searchTerm.toLowerCase();

    if (fullName.includes(searchLower)) return true;

    if (email.includes(searchLower) || phone.includes(searchLower) || 
        username.includes(searchLower) || nationality.includes(searchLower)) return true;

    const searchWords = searchLower.split(/\s+/);
    const nameWords = fullName.split(/\s+/);

    return searchWords.every(searchWord => 
      nameWords.some(nameWord => 
        nameWord.includes(searchWord) || searchWord.includes(nameWord)
      )
    );
  };

  const calculateRelevanceScore = (user: User, searchTerm: string) => {
    const fullName = `${user.firstName} ${user.middleName || ''} ${user.lastName}`.trim().toLowerCase();
    const searchLower = searchTerm.toLowerCase();
    
    let score = 0;
    
    // Exact match scores
    if (fullName === searchLower) score += 100;
    if (user.email?.toLowerCase() === searchLower) score += 80;
    if (user.username?.toLowerCase() === searchLower) score += 80;
    
    // Partial match scores
    if (fullName.includes(searchLower)) score += 50;
    
    const searchWords = searchLower.split(/\s+/);
    const nameWords = fullName.split(/\s+/);
    
    searchWords.forEach(searchWord => {
      nameWords.forEach(nameWord => {
        if (nameWord === searchWord) score += 30;
        else if (nameWord.includes(searchWord)) score += 20;
        else if (searchWord.includes(nameWord)) score += 10;
      });
    });
    
    return score;
  };

  // Smart search cho search mode
  const smartSearch = async (searchTerm: string) => {
    const parsed = parseSearchTerm(searchTerm);
    let allFoundUsers: User[] = [];
    
    const searchPromises = [];
    
    if (parsed.firstName) {
      searchPromises.push(searchWithKeyword(parsed.firstName));
    }
    
    if (parsed.lastName && parsed.lastName !== parsed.firstName) {
      searchPromises.push(searchWithKeyword(parsed.lastName));
    }
    
    if (parsed.middleName) {
      searchPromises.push(searchWithKeyword(parsed.middleName));
    }
    
    searchPromises.push(searchWithKeyword(searchTerm));

    const results = await Promise.all(searchPromises);
    
    results.forEach(result => {
      allFoundUsers = allFoundUsers.concat(result);
    });

    const uniqueUsers = allFoundUsers.filter((user, index, self) => 
      index === self.findIndex(u => u.userId === user.userId)
    );

    const matchedUsers = uniqueUsers
      .filter(user => isUserMatchSearchTerm(user, searchTerm))
      .map(user => ({
        ...user,
        relevanceScore: calculateRelevanceScore(user, searchTerm)
      }))
      .sort((a, b) => b.relevanceScore - a.relevanceScore);

    return matchedUsers;
  };

  const fetchUsersWithPagination = async () => {
    try {
      const { data: countData } = await dataProvider.userTotalCount({
        filter: '',
        count: usersPerPage
      });
      setTotalUsers(countData.count);

      const { data } = await dataProvider.getListUsers({
        filter: '',
        page: currentPage - 1,
        perPage: usersPerPage,
      });

      const mappedUsers = data.map((user: User) => ({
        ...user
      }));

      setUsers(mappedUsers);

      if (!selectedUser && mappedUsers.length > 0) {
        setSelectedUser(mappedUsers[0]);
      } else if (selectedUser) {
        const updatedSelectedUser = mappedUsers.find((u: User) => u.userId === selectedUser.userId);
        if (updatedSelectedUser) {
          setSelectedUser(updatedSelectedUser);
        }
      }
    } catch (error: any) {
      handleErrorWithLogout(error);
      setUsers([]);
      setTotalUsers(0);
      setSelectedUser(null);
    }
  };

  const fetchUsersWithSearch = async (searchTerm: string) => {
    try {
      const searchResults = await smartSearch(searchTerm);

      const mappedUsers = searchResults.map((user: User) => ({
        ...user
      }));

      const startIndex = (currentPage - 1) * usersPerPage;
      const paginatedUsers = mappedUsers.slice(startIndex, startIndex + usersPerPage);
      
      setUsers(paginatedUsers);
      setTotalUsers(mappedUsers.length);

      if (selectedUser) {
        const updatedSelectedUser = mappedUsers.find((u: User) => u.userId === selectedUser.userId);
        if (updatedSelectedUser) {
          setSelectedUser(updatedSelectedUser);
        }
      } else if (paginatedUsers.length > 0) {
        setSelectedUser(paginatedUsers[0]);
      }
    } catch (error: any) {
      handleErrorWithLogout(error);
      setUsers([]);
      setTotalUsers(0);
      setSelectedUser(null);
    }
  };

  // Main fetch function
  const fetchUsers = async () => {
    if (searchTerm.trim()) {
      setIsSearchMode(true);
      await fetchUsersWithSearch(searchTerm);
    } else {
      setIsSearchMode(false);
      await fetchUsersWithPagination();
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [currentPage, searchTerm, usersPerPage]);

  useEffect(() => {
    setCurrentPage(1); 
  }, [searchTerm]);

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsResizing(true);
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing || !containerRef.current) return;

      const containerRect = containerRef.current.getBoundingClientRect();
      const newWidth = e.clientX - containerRect.left;

      const minWidth = USER_MANAGEMENT_CONSTANTS.SIDEBAR_MIN_WIDTH;
      const maxWidth = containerRect.width * USER_MANAGEMENT_CONSTANTS.SIDEBAR_MAX_WIDTH_RATIO;

      if (newWidth >= minWidth && newWidth <= maxWidth) {
        setSidebarWidth(`${newWidth}px`);
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  const handleRefreshUser = () => {
    fetchUsers();
  };

  const totalPages = Math.ceil(totalUsers / usersPerPage);

  return (
    <Box 
      ref={containerRef}
      sx={{ 
        display: 'flex', 
        bgcolor: COLORS_ADMIN_PAGE.BACKGROUND,
        cursor: isResizing ? 'col-resize' : 'auto',
        height: USER_MANAGEMENT_CONSTANTS.CONTAINER_HEIGHT
      }}
    >
      <UserSidebar
        ref={sidebarRef}
        sidebarWidth={sidebarWidth}
        users={users}
        selectedUser={selectedUser}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        currentPage={currentPage}
        totalUsers={totalUsers}
        usersPerPage={usersPerPage}
        setCurrentPage={setCurrentPage}
        handleUserSelect={setSelectedUser}
        handleCreateUser={() => setOpenCreateModal(true)}
        onUsersPerPageChange={handleUsersPerPageChange}
        handleDeleteUser={async () => {
          if (!selectedUser) return;
          try {
            await dataProvider.deleteUser({ id: selectedUser.userId });
            
            await fetchUsers();
            
            if (users.length === 1 && currentPage > 1) {
              setCurrentPage(currentPage - 1);
            }
            
            showSuccessMessage('User deleted successfully.');
          } catch (error: any) {
            handleErrorWithLogout(error);
          }
        }}
      />
      <ResizeHandle onMouseDown={handleMouseDown} />
      <UserProfile
        selectedUser={selectedUser}
        setSelectedUser={setSelectedUser}
        handleOpenChangePasswordModal={() => setOpenChangePasswordModal(true)}
        handleSaveChanges={async () => {
          if (!selectedUser) return;
          try {
            await dataProvider.updateUser({
              id: selectedUser.userId,
              data: {
                ...selectedUser,
              },
            });
            showSuccessMessage('User updated successfully.');
            fetchUsers();
          } catch (error: any) {
            handleErrorWithLogout(error);
          }
        }}
        handleFileChange={async (event: React.ChangeEvent<HTMLInputElement>) => {
          const file = event.target.files?.[0];
          if (!file || !selectedUser) return;
          try {
            await dataProvider.uploadProfileImageUser({
              userId: selectedUser.userId,
              file,
            });
            await fetchUsers();
            showSuccessMessage('Avatar uploaded successfully.');
          } catch (error: any) {
            handleErrorWithLogout(error);
          }
        }}
        onAddressSaveSuccess={handleRefreshUser}
      />
      <CreateUserModal
        open={openCreateModal}
        handleClose={() => setOpenCreateModal(false)}
        handleCreateSubmit={async (newUserData: any) => {
          try {
            const { data } = await dataProvider.addAccount({
              data: newUserData
            });
            await fetchUsers();
            showSuccessMessage('User created successfully.');
            setOpenCreateModal(false);
          } catch (error: any) {
            handleErrorWithLogout(error);
          }
        }}
      />
      <ChangePasswordModal
        open={openChangePasswordModal}
        handleClose={() => setOpenChangePasswordModal(false)}
        selectedUser={selectedUser}
        handleChangePasswordSubmit={async (passwordData: any) => {
          if (!selectedUser) return;
          try {
            await dataProvider.changePassword({
              id: selectedUser.userId,
              currentPassword: passwordData.currentPassword,
              newPassword: passwordData.newPassword,
            });
            showSuccessMessage('Password changed successfully.');
            setOpenChangePasswordModal(false);
          } catch (error: any) {
            handleErrorWithLogout(error);
          }
        }}
      />
      <Snackbar
        open={snackbar.open}
        autoHideDuration={USER_MANAGEMENT_CONSTANTS.SNACKBAR_DURATION}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserManagement;